/**
 * Détection d'un nouveau déploiement côté client.
 *
 * Remix ne recharge la page que lorsqu'un chunk de route ne peut plus être
 * téléchargé. Un onglet ouvert depuis avant un déploiement continue donc de
 * faire tourner l'ancien bundle avec les données à jour du serveur, ce qui
 * casse dès que la forme d'un loader change (incident du 25 août 2026 sur la
 * page de financement: bundle du 14 août, `finance[ancienneClé]` →
 * `undefined.toFixed`).
 *
 * `useReloadOnNewBuild` compare l'identifiant de build embarqué dans la page
 * (`ENV.BUILD_ID`) à celui que le serveur sert (`/resources/build-id`) et
 * force un chargement complet du document quand ils diffèrent:
 * - au début d'une navigation client, avant que l'ancien module de route ne
 *   rende les nouvelles données;
 * - à la fin d'une navigation (filet de sécurité);
 * - lors d'une revalidation, explicite ou consécutive à un fetcher (ex.
 *   changement de circonscription);
 * - au retour sur l'onglet, sauf si un formulaire est en cours de saisie.
 */
import {
	useFetchers,
	useLocation,
	useNavigation,
	useRevalidator,
} from '@remix-run/react'
import { useEffect, useRef, useState } from 'react'

export const BUILD_ID_ENDPOINT = '/resources/build-id'

/** Ne pas interroger le serveur plus d'une fois par intervalle. */
export const CHECK_THROTTLE_MS = 30_000
/**
 * Garde-fou contre une boucle de rechargements (ex. déploiement en cours où
 * deux machines servent deux builds différents).
 */
export const RELOAD_COOLDOWN_MS = 2 * 60_000
const RELOAD_STORAGE_KEY = 'hub:build-reload-at'

const IGNORED_INPUT_TYPES = new Set([
	'hidden',
	'submit',
	'button',
	'reset',
	'image',
])

export async function fetchServerBuildId(): Promise<string | null> {
	try {
		const res = await fetch(BUILD_ID_ENDPOINT, {
			headers: { accept: 'application/json' },
			cache: 'no-store',
		})
		if (!res.ok) return null
		const body = (await res.json()) as { buildId?: unknown } | null
		const buildId = body?.buildId
		return typeof buildId === 'string' && buildId.length > 0 ? buildId : null
	} catch {
		return null
	}
}

/**
 * Formulaire en cours de saisie? Compare les champs non contrôlés à leur
 * valeur initiale. Les champs contrôlés par React gardent leur attribut
 * `value` synchronisé et ne sont donc pas détectés: c'est un meilleur effort
 * pour ne pas perdre le travail de l'usager, pas une garantie.
 */
export function hasDirtyForm(root: ParentNode = document): boolean {
	const fields = Array.from(
		root.querySelectorAll<
			HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
		>('input, textarea, select'),
	)
	for (const field of fields) {
		if (field instanceof HTMLSelectElement) {
			const changed = Array.from(field.options).some(
				(option) => option.selected !== option.defaultSelected,
			)
			if (changed) return true
			continue
		}
		if (field instanceof HTMLInputElement) {
			if (field.type === 'checkbox' || field.type === 'radio') {
				if (field.checked !== field.defaultChecked) return true
				continue
			}
			if (IGNORED_INPUT_TYPES.has(field.type)) continue
		}
		if (field.value !== field.defaultValue) return true
	}
	return false
}

/**
 * Chargement complet du document vers `target` (chemin + query + hash), ou
 * rechargement si c'est déjà l'URL courante.
 */
export function hardNavigate(target: string) {
	const current =
		window.location.pathname + window.location.search + window.location.hash
	if (target === current) window.location.reload()
	else window.location.assign(target)
}

function getSessionStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
	try {
		return typeof window === 'undefined' ? null : window.sessionStorage
	} catch {
		return null
	}
}

export type BuildGuardOptions = {
	/** Identifiant du build dont provient le bundle en cours d'exécution. */
	clientBuildId: string
	fetchBuildId?: () => Promise<string | null>
	navigate?: (target: string) => void
	isFormDirty?: () => boolean
	now?: () => number
	storage?: Pick<Storage, 'getItem' | 'setItem'> | null
}

export type BuildGuardCheck = {
	/** URL (chemin + query + hash) à charger si le build a changé. */
	target: string
	/** Reporter le rechargement si un formulaire est en cours de saisie. */
	deferIfDirty?: boolean
}

/**
 * Logique du garde-fou, indépendante de React: throttle des vérifications,
 * mémorisation de l'état périmé et cooldown entre deux rechargements. Les
 * dépendances sont injectables pour les tests.
 */
export function createBuildGuard({
	clientBuildId,
	fetchBuildId = fetchServerBuildId,
	navigate = hardNavigate,
	isFormDirty = hasDirtyForm,
	now = Date.now,
	storage = getSessionStorage(),
}: BuildGuardOptions) {
	let lastCheckAt = Number.NEGATIVE_INFINITY
	let stale = false
	let inFlight: Promise<boolean> | null = null

	function isStale(): Promise<boolean> {
		if (stale) return Promise.resolve(true)
		if (inFlight) return inFlight
		if (now() - lastCheckAt < CHECK_THROTTLE_MS) return Promise.resolve(false)
		lastCheckAt = now()
		inFlight = fetchBuildId()
			.then((serverBuildId) => {
				if (serverBuildId && serverBuildId !== clientBuildId) stale = true
				return stale
			})
			.finally(() => {
				inFlight = null
			})
		return inFlight
	}

	function reloadedRecently(): boolean {
		try {
			const at = Number(storage?.getItem(RELOAD_STORAGE_KEY))
			return at > 0 && now() - at < RELOAD_COOLDOWN_MS
		} catch {
			return false
		}
	}

	return {
		get stale() {
			return stale
		},
		/** Résout à `true` si un chargement complet a été déclenché. */
		async check({ target, deferIfDirty = false }: BuildGuardCheck) {
			if (!(await isStale())) return false
			if (reloadedRecently()) return false
			if (deferIfDirty && isFormDirty()) return false
			try {
				storage?.setItem(RELOAD_STORAGE_KEY, String(now()))
			} catch {
				// stockage indisponible (navigation privée, etc.): on recharge quand même
			}
			navigate(target)
			return true
		},
	}
}

export type BuildGuard = ReturnType<typeof createBuildGuard>

/**
 * Recharge l'onglet dès qu'un build plus récent que celui chargé est servi.
 * Sans `clientBuildId` (développement), ne fait rien.
 */
export function useReloadOnNewBuild(
	clientBuildId: string | null | undefined,
	options?: Omit<BuildGuardOptions, 'clientBuildId'>,
) {
	const location = useLocation()
	const navigation = useNavigation()
	const revalidator = useRevalidator()
	const fetchers = useFetchers()

	// L'identifiant du build ne change pas pendant la vie de l'onglet: un seul
	// garde par montage.
	const [guard] = useState<BuildGuard | null>(() =>
		clientBuildId ? createBuildGuard({ clientBuildId, ...options }) : null,
	)

	const currentHref = location.pathname + location.search + location.hash
	// Uniquement pendant le chargement des loaders: jamais pendant l'envoi
	// d'une action, qu'un chargement complet interromprait.
	const pending =
		navigation.state === 'loading' ? navigation.location : undefined
	const pendingHref = pending
		? pending.pathname + pending.search + pending.hash
		: null
	// Une revalidation consécutive à l'action d'un fetcher ne passe ni par
	// `useNavigation` ni par `useRevalidator`: seul le fetcher passe à
	// `loading` (avec sa méthode de soumission).
	const fetcherRevalidating = fetchers.some(
		(fetcher) =>
			fetcher.state === 'loading' &&
			fetcher.formMethod != null &&
			fetcher.formMethod.toUpperCase() !== 'GET',
	)
	const revalidating = revalidator.state === 'loading' || fetcherRevalidating

	// 1. Début de navigation: vérifier avant que l'ancien module de route ne
	// rende les nouvelles données. Si l'onglet est déjà connu périmé, la
	// destination est chargée sans attendre.
	useEffect(() => {
		if (!guard || !pendingHref) return
		void guard.check({ target: pendingHref })
	}, [guard, pendingHref])

	// 2. Fin de navigation: filet de sécurité si (1) n'a pas abouti à temps.
	// Ignoré au montage: un document fraîchement chargé est forcément à jour.
	const isFirstLocation = useRef(true)
	useEffect(() => {
		if (isFirstLocation.current) {
			isFirstLocation.current = false
			return
		}
		if (!guard) return
		void guard.check({ target: currentHref })
	}, [guard, currentHref])

	// 3. Revalidation: les loaders se ré-exécutent et l'ancien module rend
	// les nouvelles données sur place.
	useEffect(() => {
		if (!guard || !revalidating) return
		void guard.check({ target: currentHref })
	}, [guard, revalidating, currentHref])

	// 4. Retour sur l'onglet: recharger un onglet périmé avant que l'usager
	// n'y navigue, sauf si un formulaire est en cours de saisie.
	useEffect(() => {
		if (!guard) return
		const onVisible = () => {
			if (document.visibilityState !== 'visible') return
			void guard.check({ target: currentHref, deferIfDirty: true })
		}
		document.addEventListener('visibilitychange', onVisible)
		window.addEventListener('focus', onVisible)
		return () => {
			document.removeEventListener('visibilitychange', onVisible)
			window.removeEventListener('focus', onVisible)
		}
	}, [guard, currentHref])
}
